'use client';

import { Stack } from '@mui/material';
import { ProfilePicture } from './ProfilePicture';
import { ProfileInfo } from './ProfileInfo';
import { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

interface ProfileHeaderProps {
  user: {
    id: string;
    name: string;
    bio?: string | null;
    profilePicture?: { url: string } | null;
  };
  isOwnProfile: boolean;
}

export default function ProfileHeader({ user, isOwnProfile }: ProfileHeaderProps) {
  const [profilePictureUrl, setProfilePictureUrl] = useState(user.profilePicture?.url);
  const handleUploadPicture = async (file: File) => {
    // Upload file using the new API route
    const formData = new FormData();
    formData.append('file', file);

    const uploadResponse = await fetch('/api/upload', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload image');
    }

    const { url } = await uploadResponse.json();

    // Save to database
    const response = await fetch(`/api/user/profile-picture`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, name: file.name, size: file.size }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to update profile picture');
    }

    setProfilePictureUrl(url);
  };

  const handleSaveProfile = async (name: string, bio: string) => {
    const response = await fetch(`/api/user/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, bio }),
    });

    if (!response.ok) throw new Error('Failed to update profile');
  };

  return (
    <Stack spacing={3}>
      <DndProvider backend={HTML5Backend}>
        <ProfilePicture
          url={profilePictureUrl}
          name={user.name}
          isOwnProfile={isOwnProfile}
          onUpload={handleUploadPicture}
        />
      </DndProvider>
      <ProfileInfo
        name={user.name}
        bio={user.bio}
        isOwnProfile={isOwnProfile}
        onSave={handleSaveProfile}
      />
    </Stack>
  );
}
